import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Sparkles, CheckCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AIContentAction from "../actions/AIContentAction";
import ChartLoading from "../insight/components/ChartLoading";
import { Button, Modal } from "../components/ui";
import LayoutAction from "../actions/LayoutAction";
import ConfirmDialog from "../dialog/ConfirmDialog";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import { selectAiSetting, selectHasFullAiSetting } from "./selectors/aiContentSelectors";
import { useQuery } from "@tanstack/react-query";
import { suggestTopics as apiSuggestTopics } from "../api/AIContentApi";
import BillingAction from "../actions/BillingAction";
import {
  getAiContentQuotaErrorMessage,
  handleAiContentQuotaError,
} from "./utils/aiContentQuotaUtils";
export interface TopicItem {
  id: string;
  title: string;
  platform: string;
  engagement_score: number;
  description?: string;
  category?: string;
}

interface TopicsModalProps {
  onConfirm?: (selectedTopic: TopicItem) => void;
}

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  position?: "top" | "left" | "right" | "bottom";
}

const tooltipPositionStyle: Record<string, string> = {
  top: "left-0 top-full mt-2",
  bottom: "left-0 bottom-full mb-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

const Tooltip: React.FC<TooltipProps> = ({ content, children, position = "top" }) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      tabIndex={0}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div
          className={`absolute z-50 min-w-[220px] max-w-[280px] px-4 py-3 bg-black text-white text-sm rounded-xl shadow-xl text-left ${tooltipPositionStyle[position]}`}
          role="tooltip"
        >
          {content}
        </div>
      )}
    </span>
  );
};

const TopicsModal: React.FC<TopicsModalProps> = ({ onConfirm }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfirmRegenerate, setShowConfirmRegenerate] = useState(false);
  const [showConfirmGenerate, setShowConfirmGenerate] = useState(false);

  const aiContentState = useSelector((state: any) => state.aiContent);
  const { isTopicsModalOpen, selectedTopicIds } = useMemo(() => {
    const s = aiContentState?.toJS?.() || {};
    return {
      isTopicsModalOpen: s.isTopicsModalOpen,
      selectedTopicIds: s.selectedTopicIds || [],
    };
  }, [aiContentState]);

  // Use Redux selectors
  const aiSetting = useSelector(selectAiSetting);
  const hasValidAiSetting = useSelector(selectHasFullAiSetting);

  const enabled = Boolean(isTopicsModalOpen && projectId && hasValidAiSetting);

  const queryParams = useMemo(
    () => ({
      projectId,
      service_description: aiSetting?.get?.("serviceDescription"),
      customer_location: aiSetting?.get?.("customerLocation"),
      company_industry: aiSetting?.get?.("companyIndustry"),
      language: aiSetting?.get?.("language"),
    }),
    [projectId, aiSetting]
  );

  const query = useQuery({
    queryKey: ["suggestTopics", queryParams],
    enabled,
    queryFn: async ({ queryKey }: any) => {
      const [, body] = queryKey as any;
      return await apiSuggestTopics(
        body.projectId,
        body.service_description,
        body.customer_location,
        body.company_industry,
        body.language
      );
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  } as any);

  const isLoading = Boolean(query.isLoading || query.isFetching);
  const queryError: any = (query as any).error;
  const topicsData: any = useMemo(() => (query.data as any) || {}, [query.data]);

  useEffect(() => {
    if (!queryError) {
      return;
    }
    if (handleAiContentQuotaError(dispatch, queryError)) {
      if (projectId) {
        dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
      }
      return;
    }
    dispatch(
      LayoutAction.setNotification({
        title: t("Topics"),
        type: "error",
        message: t("Error generating topics: {{error}}", {
          error: String(getAiContentQuotaErrorMessage(queryError) || queryError?.message || queryError),
        }),
      }) as any
    );
  }, [queryError, dispatch, t, projectId]);

  useEffect(() => {
    if (query.isSuccess && projectId) {
      dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
    }
  }, [query.isSuccess, projectId, dispatch]);

  const getPlatformForTopic = useCallback((topic: any) => {
    const category = topic.category?.toLowerCase() || "";

    if (category.includes("professional") || category.includes("business")) {
      return "linkedin";
    } else if (category.includes("visual") || category.includes("lifestyle")) {
      return "instagram";
    } else if (category.includes("community") || category.includes("local")) {
      return "facebook";
    } else if (category.includes("news") || category.includes("trending")) {
      return "twitter";
    }

    return "instagram";
  }, []);

  const getEngagementScoreStyle = useCallback(
    (score: number) => {
      const percentage = Math.round(score * 10);

      if (percentage >= 80) {
        return {
          bg: "bg-green-100",
          text: "text-green-700",
          icon: "text-green-600",
          label: t("Excellent"),
        };
      } else if (percentage >= 60) {
        return {
          bg: "bg-blue-100",
          text: "text-blue-700",
          icon: "text-blue-600",
          label: t("Good"),
        };
      } else if (percentage >= 40) {
        return {
          bg: "bg-orange-100",
          text: "text-orange-700",
          icon: "text-orange-600",
          label: t("Fair"),
        };
      } else {
        return {
          bg: "bg-red-100",
          text: "text-red-700",
          icon: "text-red-600",
          label: t("Poor"),
        };
      }
    },
    [t]
  );

  const transformedTopics: TopicItem[] = useMemo(() => {
    const arr = (topicsData?.topics?.topics || []) as any[];
    return (
      arr
        .map((topic, index) => ({
          id: `topic-${index}`,
          title: topic.topic,
          platform: getPlatformForTopic(topic),
          engagement_score: topic.engagement_score || 0,
          description: topic.description,
          category: topic.category,
        }))
        .sort((a, b) => b.engagement_score - a.engagement_score) || []
    );
  }, [topicsData, getPlatformForTopic]);

  const total = useMemo(() => transformedTopics.length, [transformedTopics]);
  const selectedCount = useMemo(() => selectedTopicIds.length, [selectedTopicIds]);

  // Number of topics to generate (can be made configurable)
  const topicsToGenerate = 6;

  const selectedTopicIdsSet = useMemo(() => new Set(selectedTopicIds), [selectedTopicIds]);
  const selectedTopic = useMemo(
    () => transformedTopics.find((topic) => selectedTopicIdsSet.has(topic.id)),
    [transformedTopics, selectedTopicIdsSet]
  );

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      dispatch(AIContentAction.closeTopicsModal() as any);
      setIsAnimating(false);
    }, 150);
  };

  const handleRegenerateTopics = useCallback(() => {
    setShowConfirmRegenerate(true);
  }, []);

  const handleConfirmRegenerate = useCallback(() => {
    if (!hasValidAiSetting) {
      console.warn("aiSetting is missing required data for suggestTopics");
      return;
    }
    query.refetch();
    setShowConfirmRegenerate(false);

    // Removed onInsert flow
  }, [hasValidAiSetting, query]);

  const handleConfirm = useCallback(() => {
    if (!selectedTopic) return;

    // Get current AI setting topic
    const currentAiTopic = aiSetting?.get("topic") || "";
    const selectedTopicTitle = selectedTopic.title;

    // Check if the selected topic is the same as current AI setting topic
    if (currentAiTopic && currentAiTopic === selectedTopicTitle) {
      setShowConfirmGenerate(true);
    } else {
      // Different topic, proceed directly
      handleConfirmGenerate();
    }
  }, [selectedTopic, aiSetting, handleConfirmGenerate]);

  const handleConfirmGenerate = useCallback(() => {
    if (selectedTopic && onConfirm) {
      dispatch(AIContentAction.confirmTopicSelection(selectedTopic) as any);
      onConfirm(selectedTopic);
      setShowConfirmGenerate(false);
    }
  }, [selectedTopic, onConfirm, dispatch]);

  const handleTopicSelect = useCallback(
    (topicId: string) => {
      if (selectedTopicIdsSet.has(topicId)) {
        dispatch(AIContentAction.deselectTopic(topicId) as any);
      } else {
        dispatch(AIContentAction.deselectAllTopics() as any);
        dispatch(AIContentAction.selectTopic(topicId) as any);
      }
    },
    [selectedTopicIdsSet, dispatch]
  );

  const modalContent = (
    <div className="px-6 pb-4">
      <div>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ChartLoading label={t("Loading suggested topics...")} />
          </div>
        ) : (
          <div className="space-y-3 pt-3">
            {transformedTopics.map((topic) => {
              const checked = selectedTopicIdsSet.has(topic.id);
              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => handleTopicSelect(topic.id)}
                  className={`w-full text-left rounded-xl border bg-white hover:bg-gray-50 transition-all duration-200 ${
                    checked ? "border-blue-400 ring-2 ring-blue-200 bg-blue-50" : "border-gray-200"
                  } ${isAnimating ? "opacity-50" : ""}`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Left Section - Icon and Percentage */}
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className={`inline-flex items-center justify-center rounded-full ${
                            getEngagementScoreStyle(topic.engagement_score).icon
                          }`}
                        >
                          <Sparkles className="w-[18px] h-[18px]" />
                        </div>
                        <div className="text-center">
                          <span
                            className={`text-lg font-bold ${
                              getEngagementScoreStyle(topic.engagement_score).text
                            }`}
                          >
                            {Math.round(topic.engagement_score * 10)}%
                          </span>
                        </div>
                      </div>

                      {/* Right Section - Main Content */}
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-gray-900 leading-5 mb-2">
                          {topic.title}
                        </div>

                        {/* Category Tag */}
                        {topic.category && (
                          <div className="inline-flex items-center">
                            <span className="px-3 py-1 rounded-full text-xs font-medium border border-blue-300 bg-white text-blue-600">
                              {topic.category}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          {t("Cancel")}
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isLoading || selectedCount === 0}
          leftIcon={<CheckCircle className="w-4 h-4" />}
          className="bg-teal-600 hover:bg-teal-700"
        >
          {t("Generate Content")}
        </Button>
        <Button
          variant="primary"
          onClick={handleRegenerateTopics}
          disabled={isLoading}
          leftIcon={
            isLoading ? (
              <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )
          }
        >
          {isLoading
            ? t("Generating...")
            : t("Generate {{count}} New Topics", { count: topicsToGenerate })}
        </Button>
      </div>
    </div>
  );

  const headerTooltip = (
    <div className="w-5 h-5 flex items-center justify-start mt-1">
      <Tooltip
        position="left"
        content={
          <div>
            <div className="font-semibold text-base mb-1 leading-tight">
              {t("AI Relevance Score")}
            </div>
            <div className="text-xs text-gray-300 mb-2 leading-snug">
              {t("Understand color code")}
            </div>
            <div className="space-y-1 text-[15px]">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 border border-white/70" />
                {t("Excellent")}
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 border border-white/70" />
                {t("Good")}
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 border border-white/70" />
                {t("Fair")}
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 border border-white/70" />
                {t("Poor")}
              </div>
            </div>
          </div>
        }
      >
        <button
          type="button"
          aria-label={t("AI Relevance Score Info")}
          className="mr-2 p-1 rounded-full hover:bg-gray-100"
        >
          <Info className="w-5 h-5 text-black" />
        </button>
      </Tooltip>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isTopicsModalOpen}
        onClose={handleClose}
        leftExtend={headerTooltip}
        title={t("AI Suggested Topics")}
        subtitle={t(
          "Here are {{count}} AI-generated topics based on your profile, each with an AI relevance score and catalog reference",
          { count: total }
        )}
        footer={modalFooter}
        className="h-[892px] max-h-[90vh] w-[531px]"
      >
        {modalContent}
      </Modal>

      <ConfirmDialog
        visible={showConfirmRegenerate}
        title={t("Regenerate Topics")}
        description={t(
          "Are you sure you want to generate {{count}} new topics? This will replace the current topics.",
          { count: topicsToGenerate }
        )}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleConfirmRegenerate}
        onClose={() => setShowConfirmRegenerate(false)}
      />

      <ConfirmDialog
        visible={showConfirmGenerate}
        title={t("Confirm AI Topic")}
        description={t(
          "You are selecting the same topic '{{topic}}' that is already set in your AI profile. Do you want to continue with content generation?",
          {
            topic: selectedTopic?.title || "",
          }
        )}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleConfirmGenerate}
        onClose={() => setShowConfirmGenerate(false)}
      />
    </>
  );
};

export default TopicsModal;
